import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { User, Save, Camera } from 'lucide-react'
import { Modal } from '../ui/Modal'
import { FormField } from '../form/FormField'
import { useUsuario } from '../../contexts/UsuarioContext'
import { usuarioService } from '../../dominios/usuarios/services/usuario'
import { useToast } from '../../contexts/NotificationContext'
import { useQueryClient } from 'react-query'

const perfilSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().email('Email inválido'),
  senhaAtual: z.string().optional(),
  novaSenha: z.string().optional(),
  confirmarSenha: z.string().optional(),
}).superRefine((data, ctx) => {
  if (data.novaSenha) {
    if (!data.senhaAtual) {
      ctx.addIssue({
        path: ['senhaAtual'],
        code: z.ZodIssueCode.custom,
        message: 'Senha atual é obrigatória para alterar a senha',
      })
    }
    if (data.novaSenha.length < 6) {
      ctx.addIssue({
        path: ['novaSenha'],
        code: z.ZodIssueCode.custom,
        message: 'Nova senha deve ter pelo menos 6 caracteres',
      })
    }
    if (data.novaSenha !== data.confirmarSenha) {
      ctx.addIssue({
        path: ['confirmarSenha'],
        code: z.ZodIssueCode.custom,
        message: 'As senhas devem ser iguais',
      })
    }
  }
})

type PerfilFormData = z.infer<typeof perfilSchema>

interface ModalPerfilProps {
  isOpen: boolean
  onClose: () => void
}

export function ModalPerfil({ isOpen, onClose }: ModalPerfilProps) {
  const { usuario, setUsuario } = useUsuario()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [foto, setFoto] = useState<File | null>(null)
  const [fotoPreview, setFotoPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PerfilFormData>({
    resolver: zodResolver(perfilSchema),
    defaultValues: {
      nome: usuario?.nome || '',
      email: usuario?.email || '',
      senhaAtual: '',
      novaSenha: '',
      confirmarSenha: '',
    },
  })

  useEffect(() => {
    if (usuario && isOpen) {
      reset({
        nome: usuario.nome || '',
        email: usuario.email || '',
        senhaAtual: '',
        novaSenha: '',
        confirmarSenha: '',
      })
      setFoto(null)
      setFotoPreview(null)
    }
  }, [usuario, isOpen, reset])

  const handleFotoSelect = (file: File | null) => {
    setFoto(file)
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setFotoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    } else {
      setFotoPreview(null)
    }
  }

  const handleFotoClick = () => {
    fileInputRef.current?.click()
  }

  const onSubmit = async (data: PerfilFormData) => {
    if (!usuario) return

    try {
      await usuarioService.atualizarPerfil({
        nome: data.nome,
        email: data.email,
        senhaAtual: data.senhaAtual || undefined,
        novaSenha: data.novaSenha || undefined,
        foto: foto || undefined,
      })

      // Atualizar usuário no contexto
      const usuarioAtualizado = await queryClient.fetchQuery(['usuario-autenticado'], async () => {
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5160/api'}/Auth/me`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        })
        return response.json()
      })

      setUsuario({
        ...usuario,
        nome: data.nome,
        email: data.email,
        fotoUrl: usuarioAtualizado.fotoUrl || usuario.fotoUrl,
      })

      queryClient.invalidateQueries(['menu'])

      toast.success('Perfil atualizado com sucesso!')
      onClose()
    } catch (error: any) {
      const mensagem = error?.response?.data || error?.message || 'Erro ao atualizar perfil'
      toast.error(typeof mensagem === 'string' ? mensagem : 'Ocorreu um erro ao atualizar o perfil')
    }
  }

  const fotoUrlAtual = usuario?.fotoUrl
    ? `${import.meta.env.VITE_API_URL || 'http://localhost:5160/api'}${usuario.fotoUrl}`
    : null

  const fotoParaExibir = fotoPreview || fotoUrlAtual

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Meu Perfil"
      icon={
        <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-white" />
        </div>
      }
      size="2xl"
      footer={
        <div className="flex justify-end gap-3 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            className="px-5 py-2.5 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium"
          >
            <Save className="w-4 h-4" />
            {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Foto - Centralizada no topo */}
        <div className="flex justify-center">
          <div className="space-y-3">
            <div className="relative inline-block">
              <div 
                className="w-40 h-40 rounded-full overflow-hidden border-4 border-gray-200 bg-gray-50 flex items-center justify-center group cursor-pointer hover:border-brand-400 transition-all shadow-lg"
                onClick={handleFotoClick}
              >
                {fotoParaExibir ? (
                  <img
                    src={fotoParaExibir}
                    alt="Foto do perfil"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-gray-400">
                    <Camera className="w-12 h-12 mb-2" />
                    <p className="text-xs font-medium">Adicionar</p>
                  </div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center rounded-full">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="w-10 h-10 text-white" />
                  </div>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={(e) => handleFotoSelect(e.target.files?.[0] || null)}
                className="hidden"
              />
            </div>
            <p className="text-xs text-gray-500 text-center">Clique na foto para alterar</p>
            <p className="text-xs text-gray-400 text-center">JPG ou PNG, máximo 5MB</p>
          </div>
        </div>

        {/* Informações Pessoais */}
        <div className="space-y-4 pt-4 border-t border-gray-200">
          <div className="pb-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Informações Pessoais</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nome */}
            <FormField label="Nome Completo" error={errors.nome}>
              <input
                type="text"
                {...register('nome')}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all ${
                  errors.nome ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </FormField>

            {/* Email */}
            <FormField label="E-mail" error={errors.email}>
              <input
                type="email"
                {...register('email')}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </FormField>
          </div>
        </div>

        {/* Alteração de Senha */}
        <div className="space-y-4 pt-4 border-t border-gray-200">
          <div className="pb-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Alterar Senha</h3>
            <p className="text-xs text-gray-500 mt-1">Deixe os campos em branco se não desejar alterar a senha</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Senha Atual */}
            <FormField label="Senha Atual" error={errors.senhaAtual}>
              <input
                type="password"
                {...register('senhaAtual')}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all ${
                  errors.senhaAtual ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Digite sua senha atual"
              />
            </FormField>

            {/* Nova Senha */}
            <FormField label="Nova Senha" error={errors.novaSenha}>
              <input
                type="password"
                {...register('novaSenha')}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all ${
                  errors.novaSenha ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Digite a nova senha"
              />
            </FormField>

            {/* Confirmar Senha */}
            <FormField label="Confirmar Nova Senha" error={errors.confirmarSenha}>
              <input
                type="password"
                {...register('confirmarSenha')}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all ${
                  errors.confirmarSenha ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Confirme a nova senha"
              />
            </FormField>
          </div>
        </div>
      </form>
    </Modal>
  )
}
